import { useState, useEffect, useRef } from "react";

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const inputRef = useRef(null);

  // Global keyboard listener
  useEffect(() => {
    function onKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(o => !o);
      }
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
      setQuery("");
      setResults([]);
      setRunResult(null);
    }
  }, [open]);

  // Search as you type
  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/actions/search?q=${encodeURIComponent(query)}&limit=10`);
        if (res.ok) setResults(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [query]);

  async function execute(actionKey) {
    setExecuting(actionKey);
    setRunResult(null);
    try {
      const res = await fetch(`/api/actions/${actionKey}/execute`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input_parameters: {} }),
      });
      const data = await res.json();
      setRunResult({ key: actionKey, ...data });
    } catch (e) {
      setRunResult({ key: actionKey, error: e.message });
    }
    setExecuting(null);
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg border overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Search bar */}
        <div className="flex items-center px-4 border-b">
          <svg className="w-5 h-5 text-gray-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search actions... (Ctrl+K)"
            className="w-full py-3 text-sm outline-none bg-transparent"
          />
          <kbd className="text-[10px] text-gray-400 border px-1.5 py-0.5 rounded bg-gray-50 ml-2">ESC</kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {loading && <div className="px-4 py-6 text-center text-gray-400 text-sm">Searching...</div>}
          {!loading && query && results.length === 0 && <div className="px-4 py-6 text-center text-gray-400 text-sm">No actions found</div>}
          {!loading && results.map(a => (
            <button
              key={a.id}
              onClick={() => execute(a.action_key)}
              disabled={executing === a.action_key}
              className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 text-left border-b last:border-0"
            >
              <span className="text-lg">{a.icon || "\u26A1"}</span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{a.title}</div>
                <div className="text-xs text-gray-500 truncate">{a.description}</div>
              </div>
              {a.category && <span className="text-[10px] px-2 py-0.5 bg-gray-100 rounded text-gray-500">{a.category}</span>}
              {executing === a.action_key && <span className="text-xs text-indigo-600 animate-pulse">Running...</span>}
            </button>
          ))}
        </div>

        {/* Run result */}
        {runResult && (
          <div className="px-4 py-3 border-t bg-gray-50">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-700">
                {runResult.error ? "Error" : `${runResult.key}: ${runResult.status || "done"}`}
              </span>
              <button onClick={() => setRunResult(null)} className="text-gray-400 text-xs hover:text-gray-600">&times;</button>
            </div>
            {runResult.error && <div className="text-xs text-red-600">{runResult.error}</div>}
            {runResult.id && <div className="text-[10px] text-gray-500 font-mono">Run ID: {runResult.id}</div>}
          </div>
        )}

        {/* Footer */}
        {!query && !runResult && (
          <div className="px-4 py-3 border-t text-xs text-gray-400 text-center">
            Type to search scripted actions
          </div>
        )}
      </div>
    </div>
  );
}
